import {
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Paid as PaidIcon,
  Sync as SyncIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import MarketGrowthAdminApi from "../../../../../infrastructure/api/marketGrowthAdmin";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import { formatMoney } from "../../../partner-commission/utils/formatMoney";
import { PARTNER_PAYOUT_STATUS_LABELS } from "../../../partner-commission/constants/portalEnums";
import AdminActionConfirmDialog from "../components/AdminActionConfirmDialog";
import AdminPayoutDetailDialog from "../components/AdminPayoutDetailDialog";
import MarketGrowthDataTable, {
  MarketGrowthServerPagination,
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthExportButton from "../components/MarketGrowthExportButton";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import MarketGrowthStatCard from "../components/MarketGrowthStatCard";
import { getEnumLabel, toSelectOptions } from "../constants/commissionEnums";
import { formatApiDate } from "../utils/commissionValidation";
import { openMarketGrowthExportLink } from "../utils/exportHelpers";

const PAGE_SIZE = 20;

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const getPayoutActions = (status) => {
  const s = String(status || "");
  if (s === "Requested") return ["approve", "reject"];
  if (s === "Approved") return ["mark-paid"];
  return [];
};

const ACTION_CONFIG = {
  approve: {
    title: "تأیید درخواست تسویه",
    description: "آیا این درخواست تسویه تأیید شود؟",
    confirmLabel: "تأیید",
    confirmColor: "success",
  },
  reject: {
    title: "رد درخواست تسویه",
    description: "آیا این درخواست تسویه رد شود؟",
    confirmLabel: "رد",
    confirmColor: "error",
    notesLabel: "دلیل رد (اختیاری)",
  },
  "mark-paid": {
    title: "ثبت پرداخت تسویه",
    description: "آیا پرداخت این تسویه ثبت شود؟",
    confirmLabel: "ثبت پرداخت",
    confirmColor: "success",
  },
};

const PartnerPayoutsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [partners, setPartners] = useState([]);
  const [filters, setFilters] = useState({
    partnerId: "",
    status: "",
    fromDate: null,
    toDate: null,
  });
  const [detailId, setDetailId] = useState(null);
  const [actionState, setActionState] = useState({ open: false, row: null, action: null });
  const [saving, setSaving] = useState(false);

  const loadPartners = useCallback(async () => {
    const res = await MarketGrowthAdminApi.getPartners();
    const normalized = MarketGrowthAdminApi.normalizeList(res, "بارگذاری نمایندگان ناموفق بود.");
    if (!normalized.error) {
      setPartners(Array.isArray(normalized.data) ? normalized.data : []);
    }
  }, []);

  const buildParams = useCallback(
    () => {
      const params = { page, pageSize: PAGE_SIZE };
      if (filters.partnerId) params.partnerId = Number(filters.partnerId);
      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
      if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
      return params;
    },
    [filters, page]
  );

  const buildExportParams = useCallback(() => {
    const params = {};
    if (filters.partnerId) params.partnerId = Number(filters.partnerId);
    if (filters.status) params.status = filters.status;
    if (filters.fromDate) params.fromDate = formatApiDate(filters.fromDate);
    if (filters.toDate) params.toDate = formatApiDate(filters.toDate);
    return params;
  }, [filters]);

  const handleExport = useCallback(async () => {
    const res = await MarketGrowthAdminApi.exportPayouts(buildExportParams());
    const { error: err, data } = MarketGrowthAdminApi.normalize(
      res,
      "خروجی اکسل تسویه‌ها ناموفق بود."
    );
    if (err) throw new Error(err);
    openMarketGrowthExportLink(data);
  }, [buildExportParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await MarketGrowthAdminApi.getPayouts(buildParams());
      const { error: err, data } = MarketGrowthAdminApi.normalize(
        res,
        "بارگذاری تسویه‌ها ناموفق بود."
      );
      if (err) {
        setError(err);
        setItems([]);
        setTotalCount(0);
        return;
      }
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotalCount(Number(data?.totalCount) || 0);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  useEffect(() => {
    load();
  }, [load]);

  const partnerLabelMap = useMemo(
    () =>
      partners.reduce((acc, p) => {
        acc[p.id] = `کاربر ${p.userId} (${p.referralCode || p.id})`;
        return acc;
      }, {}),
    [partners]
  );

  const summary = useMemo(() => {
    const totals = { requested: 0, approved: 0, paid: 0 };
    items.forEach((row) => {
      const amount = Number(row.amount) || 0;
      const s = String(row.status || "");
      if (s === "Requested") totals.requested += amount;
      else if (s === "Approved") totals.approved += amount;
      else if (s === "Paid") totals.paid += amount;
    });
    return totals;
  }, [items]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const resolvePartnerLabel = (row) =>
    row.partnerDisplayName || partnerLabelMap[row.partnerId] || row.partnerId;

  const applyFilters = () => {
    if (page !== 1) setPage(1);
    else load();
  };

  const runAction = async (notes) => {
    if (!actionState.row || !actionState.action) return;
    setSaving(true);
    try {
      const id = actionState.row.payoutId;
      let res;
      switch (actionState.action) {
        case "approve":
          res = await MarketGrowthAdminApi.approvePayout(id, notes);
          break;
        case "reject":
          res = await MarketGrowthAdminApi.rejectPayout(id, notes);
          break;
        case "mark-paid":
          res = await MarketGrowthAdminApi.markPayoutPaid(id, notes);
          break;
        default:
          return;
      }
      const normalized = MarketGrowthAdminApi.normalize(res, "عملیات ناموفق بود.");
      if (normalized.error) {
        toast.error(normalized.error);
        return;
      }
      toast.success("عملیات با موفقیت انجام شد.");
      setActionState({ open: false, row: null, action: null });
      await load();
    } catch (e) {
      toast.error(e?.message || "خطا در انجام عملیات");
    } finally {
      setSaving(false);
    }
  };

  const actionConfig = actionState.action ? ACTION_CONFIG[actionState.action] : null;

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        partnerLabel: resolvePartnerLabel(row),
        amountLabel: formatMoney(row.amount),
        statusLabel: getEnumLabel(PARTNER_PAYOUT_STATUS_LABELS, row.status),
      })),
    [items, partnerLabelMap]
  );

  const headCells = useMemo(
    () => [
      { id: "partnerLabel", label: "نماینده" },
      { id: "amountLabel", label: "مبلغ" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
      { id: "requestedAt", label: "تاریخ درخواست", type: "date" },
      { id: "approvedAt", label: "تاریخ تأیید", type: "date" },
      { id: "paidAt", label: "تاریخ پرداخت", type: "date" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <InfoIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setDetailId(row.payoutId),
        color: "primary",
        tooltip: "جزئیات",
      },
      {
        icon: <CheckIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "approve" }),
        color: "success",
        tooltip: "تأیید",
        hidden: (row) => !getPayoutActions(row.status).includes("approve"),
      },
      {
        icon: <PaidIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "mark-paid" }),
        color: "primary",
        tooltip: "ثبت پرداخت",
        hidden: (row) => !getPayoutActions(row.status).includes("mark-paid"),
      },
      {
        icon: <CloseIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => setActionState({ open: true, row, action: "reject" }),
        color: "error",
        tooltip: "رد",
        hidden: (row) => !getPayoutActions(row.status).includes("reject"),
      },
    ],
    []
  );

  return (
    <MarketGrowthPageShell
      title="تسویه حساب‌ها"
      description="مدیریت درخواست‌های تسویه نمایندگان."
      loading={loading}
      error={error}
      empty={!loading && !error && items.length === 0}
      onRetry={load}
    >
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <MarketGrowthStatCard
            label="درخواست‌شده"
            value={formatMoney(summary.requested)}
            subtitle="خلاصه صفحه جاری"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MarketGrowthStatCard
            label="تأییدشده"
            value={formatMoney(summary.approved)}
            subtitle="خلاصه صفحه جاری"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <MarketGrowthStatCard
            label="پرداخت‌شده"
            value={formatMoney(summary.paid)}
            subtitle="خلاصه صفحه جاری"
          />
        </Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>نماینده</InputLabel>
                <Select
                  label="نماینده"
                  value={filters.partnerId}
                  onChange={(e) => setFilters((f) => ({ ...f, partnerId: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {partners.map((p) => (
                    <MenuItem key={p.id} value={String(p.id)}>
                      {partnerLabelMap[p.id]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>وضعیت</InputLabel>
                <Select
                  label="وضعیت"
                  value={filters.status}
                  onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                >
                  <MenuItem value="">همه</MenuItem>
                  {toSelectOptions(PARTNER_PAYOUT_STATUS_LABELS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <JalaliDatePicker
                label="از تاریخ"
                value={filters.fromDate}
                onChange={(value) => setFilters((f) => ({ ...f, fromDate: value }))}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <JalaliDatePicker
                label="تا تاریخ"
                value={filters.toDate}
                onChange={(value) => setFilters((f) => ({ ...f, toDate: value }))}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={applyFilters}>
                  اعمال فیلتر
                </Button>
                <Button variant="outlined" startIcon={<SyncIcon />} onClick={load}>
                  بروزرسانی
                </Button>
                <MarketGrowthExportButton onExport={handleExport} />
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <MarketGrowthDataTable
            rowKey="payoutId"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            pageSize={PAGE_SIZE}
            showPaging={false}
            defaultSort={{ orderBy: "requestedAt", order: "desc" }}
            tableMinWidth={900}
            actionsMode="menu"
          />
          <MarketGrowthServerPagination
            page={page}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      <AdminPayoutDetailDialog
        open={Boolean(detailId)}
        payoutId={detailId}
        onClose={() => setDetailId(null)}
      />

      {actionConfig ? (
        <AdminActionConfirmDialog
          open={actionState.open}
          title={actionConfig.title}
          description={actionConfig.description}
          confirmLabel={actionConfig.confirmLabel}
          confirmColor={actionConfig.confirmColor}
          notesLabel={actionConfig.notesLabel}
          saving={saving}
          onClose={() => setActionState({ open: false, row: null, action: null })}
          onConfirm={runAction}
        />
      ) : null}
    </MarketGrowthPageShell>
  );
};

export default PartnerPayoutsPage;
